import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { useNavigate, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import {
  Lightning, ChartLineUp, MagnifyingGlass,
  ArrowRight, CheckCircle, CalendarBlank, Buildings, Bookmark
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from "recharts";
import axios from "axios";

const COLORS = ["#10B981", "#059669", "#047857", "#065F46", "#064E3B", "#34D399", "#6EE7B7", "#A7F3D0"];

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-5" data-testid={`impact-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
      <Icon className={`w-5 h-5 ${accent || 'text-emerald-500'}`} />
    </div>
    <p className="text-3xl font-bold tracking-tight">{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
  </div>
);

const RenewalCard = ({ grant }) => {
  const daysLeft = Math.ceil((new Date(grant.deadline) - new Date()) / (1000 * 60 * 60 * 24));
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 hover:border-emerald-500/30 transition-colors" data-testid={`renewal-${grant.grant_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <span className="text-[10px] uppercase tracking-wider text-emerald-500 font-semibold">{grant.category}</span>
          <h4 className="text-sm font-semibold mt-1">{grant.title}</h4>
        </div>
        {daysLeft > 0 && (
          <span className={`text-xs px-2 py-0.5 rounded-sm flex-shrink-0 ${daysLeft <= 14 ? 'bg-yellow-500/10 text-yellow-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
            {daysLeft}d left
          </span>
        )}
      </div>
      <p className="text-xs text-zinc-500 mb-2">
        ${grant.amount_min?.toLocaleString()} - ${grant.amount_max?.toLocaleString()} &middot; {grant.funder_name}
      </p>
      <div className="flex flex-wrap gap-1 mb-3">
        {(grant.match_reasons || []).map((r, i) => (
          <span key={i} className="text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-sm px-2 py-0.5">{r}</span>
        ))}
      </div>
      <Link
        to={`/grants/${grant.grant_id}`}
        className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
      >
        View & Apply <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-xs">
        <p className="text-zinc-400">{label}</p>
        <p className="text-emerald-400 font-semibold">{payload[0].value}</p>
      </div>
    );
  }
  return null;
};

const FundingTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-xs">
        <p className="text-zinc-400">{payload[0].name}</p>
        <p className="text-emerald-400 font-semibold">${payload[0].value?.toLocaleString()}</p>
      </div>
    );
  }
  return null;
};

export default function ImpactDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [impact, setImpact] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewalsLoading, setRenewalsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [impactRes, renewalRes] = await Promise.all([
          axios.get(`${API}/impact`),
          axios.get(`${API}/grants/renewals`)
        ]);
        setImpact(impactRes.data);
        setRenewals(renewalRes.data.renewals || []);
      } catch (err) {
        if (err.response?.status === 403) {
          // Not premium — that's handled in the UI
        }
        console.error("Error loading impact data:", err);
      } finally {
        setLoading(false);
        setRenewalsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Premium gate
  if (!loading && user?.subscription_tier !== "pro" && user?.role !== "admin") {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="impact-premium-gate">
          <Lightning className="w-16 h-16 text-emerald-500 mb-6" />
          <h1 className="text-3xl font-bold mb-3">Impact Dashboard</h1>
          <p className="text-zinc-400 max-w-md mb-8">
            Track your cumulative funding, success rate, and grant history. See your nonprofit's full impact story — perfect for board reports.
          </p>
          <Button
            onClick={() => navigate("/pricing")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
            data-testid="impact-upgrade-btn"
          >
            <Lightning className="w-4 h-4 mr-2" />
            Upgrade to Premium
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const s = impact?.summary || {};
  const categoryData = impact?.category_breakdown || [];
  const fundingData = impact?.category_funding || [];
  const timeline = impact?.monthly_timeline || [];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="impact-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Impact Dashboard</h1>
          <p className="text-zinc-400 mt-1">Your nonprofit's grant performance at a glance</p>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Lightning}
            label="Total Funding Won"
            value={`$${s.total_funding_won?.toLocaleString() || '0'}`}
            sub={s.active_post_grants > 0 ? `${s.active_post_grants} active grant${s.active_post_grants > 1 ? 's' : ''}` : undefined}
          />
          <StatCard
            icon={MagnifyingGlass}
            label="Success Rate"
            value={`${s.success_rate || 0}%`}
            sub={`${s.total_approved || 0} won / ${s.total_submitted || 0} submitted`}
          />
          <StatCard
            icon={ChartLineUp}
            label="Total Applied"
            value={s.total_applied || 0}
            sub={`${s.in_progress || 0} in progress`}
          />
          <StatCard
            icon={ChartLineUp}
            label="Budget Utilized"
            value={`$${s.total_expenses?.toLocaleString() || '0'}`}
            sub={s.total_funding_won > 0 ? `${Math.round((s.total_expenses / s.total_funding_won) * 100)}% of funding` : undefined}
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Applications by Category */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-5" data-testid="category-chart">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-4">Applications by Category</h3>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={categoryData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <XAxis
                    dataKey="category"
                    tick={{ fontSize: 11, fill: '#71717a' }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#52525b' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(16,185,129,0.05)' }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {categoryData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-zinc-600 text-sm text-center py-12">Apply to grants to see your category breakdown</p>
            )}
          </div>

          {/* Funding by Category (Pie) */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-5" data-testid="funding-chart">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-4">Funding by Category</h3>
            {fundingData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="60%" height={250}>
                  <PieChart>
                    <Pie
                      data={fundingData}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      innerRadius={50}
                      strokeWidth={0}
                    >
                      {fundingData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<FundingTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {fundingData.map((d, i) => (
                    <div key={d.category} className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-zinc-400 flex-1 truncate">{d.category}</span>
                      <span className="text-zinc-300 font-medium">${d.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-zinc-600 text-sm text-center py-12">Win grants to see funding breakdown</p>
            )}
          </div>
        </div>

        {/* Application Timeline */}
        {timeline.length > 0 && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-5" data-testid="timeline-chart">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-4">Application Activity</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={timeline} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#52525b' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#10B981', strokeWidth: 1, strokeDasharray: '4 4' }} />
                <Area type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} fill="url(#areaGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Grant Renewal Opportunities */}
        <div data-testid="renewal-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <CalendarBlank className="w-5 h-5 text-emerald-500" />
                Grant Renewal Opportunities
              </h2>
              <p className="text-sm text-zinc-500 mt-1">Grants from funders and categories you've won before</p>
            </div>
          </div>

          {renewalsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : renewals.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {renewals.map(g => (
                <RenewalCard key={g.grant_id} grant={g} />
              ))}
            </div>
          ) : (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-8 text-center">
              <Buildings className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">No renewal opportunities yet</p>
              <p className="text-zinc-600 text-xs mt-1">Win your first grant and we'll track future opportunities from the same funders</p>
            </div>
          )}
        </div>

        {/* FlashCare Impact Imports */}
        {(impact?.flashcare_imports || []).length > 0 && (
          <div data-testid="flashcare-imports">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-400" />
              FlashCare Impact Data
            </h2>
            <div className="space-y-3">
              {impact.flashcare_imports.map((imp, i) => (
                <div key={imp.import_id || i} className="bg-zinc-900/50 border border-blue-500/20 rounded-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium">{imp.organization}</p>
                      <p className="text-xs text-zinc-500">{imp.period}</p>
                    </div>
                    <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-sm px-1.5 py-0.5 font-semibold">FlashCare</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div>
                      <p className="text-xs text-zinc-500">Clients Served</p>
                      <p className="text-lg font-bold text-blue-400">{imp.total_clients_served?.toLocaleString()}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-zinc-500">Key Outcomes</p>
                      <p className="text-sm text-zinc-300">{imp.key_outcomes}</p>
                    </div>
                  </div>
                  {imp.demographics_summary && (
                    <p className="text-xs text-zinc-500 mt-2">Demographics: {imp.demographics_summary}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Wins */}
        {(impact?.recent_wins || []).length > 0 && (
          <div data-testid="recent-wins">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-emerald-500" />
              Recent Wins
            </h2>
            <div className="space-y-3">
              {impact.recent_wins.map((win, i) => (
                <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-sm font-medium">{win.grant_title}</p>
                      <p className="text-xs text-zinc-500">
                        {win.created_at ? new Date(win.created_at).toLocaleDateString() : ''} &middot;
                        <span className={`ml-1 ${win.status === 'active' ? 'text-emerald-400' : 'text-zinc-400'}`}>
                          {win.status === 'active' ? 'Active' : 'Completed'}
                        </span>
                      </p>
                    </div>
                  </div>
                  <span className="text-emerald-400 font-bold">${win.awarded_amount?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
